import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { useI18n } from '../i18n/I18nContext';
import { getLedgerEntries, LedgerEntry } from '../firebase/firestore';
import {
  NotionTable,
  NotionPill,
  NotionPerson,
  NotionIcons,
  type NotionColumn,
  type NotionPillColor,
} from '../components/notion/NotionTable';
import { useNotionMessages } from '../components/notion/useNotionMessages';
import './Clients.css';

/* ------------------------------------------------------------------ */
/* Modèle                                                              */
/* ------------------------------------------------------------------ */

type Purchase = {
  id: string;
  clientKey: string;
  clientName: string;
  username?: string;
  telegramUserId?: string;
  product: string;
  invoiceId?: string;
  amountStars: number;
  date: Date;
};

type Client = {
  key: string;
  name: string;
  username?: string;
  telegramUserId?: string;
  purchases: Purchase[];
  count: number;
  total: number;
  lastDate: Date;
  lastProduct: string;
  status: 'new' | 'returning' | 'vip';
};

const VIP_THRESHOLD_STARS = 10000;

type ViewKey = 'clients' | 'purchases';

const Clients: React.FC = () => {
  const { firebaseUser } = useAuth();
  const { includesInvoice } = useProjects();
  const { t } = useI18n();
  const { table: tt, clients: tc, locale } = useNotionMessages();

  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<ViewKey>('clients');
  const [detail, setDetail] = useState<Client | null>(null);

  useEffect(() => {
    if (!firebaseUser) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await getLedgerEntries(firebaseUser.uid, undefined, 1000);
        if (!cancelled) {
          setEntries(Array.isArray(data) ? data : []);
          setError(null);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || tc.failedLoad);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseUser]);

  /* ---------- Agrégation ---------- */
  const purchases: Purchase[] = useMemo(
    () =>
      entries
        .filter((e) => e.type === 'GROSS' && e.amountStars > 0 && includesInvoice(e.invoiceId))
        .map((e) => {
          const name =
            e.payerName ||
            (e.payerUsername ? `@${e.payerUsername}` : '') ||
            (e.payerTelegramUserId ? `Telegram #${e.payerTelegramUserId}` : tc.anonymous);
          const clientKey = e.payerTelegramUserId || e.payerUsername || `anon-${e.id}`;
          return {
            id: e.id,
            clientKey,
            clientName: name,
            username: e.payerUsername || undefined,
            telegramUserId: e.payerTelegramUserId || undefined,
            product: e.invoice?.title || (e.invoiceId ? t.app.dashboard.invoiceLabel.replace('{id}', e.invoiceId.slice(0, 8)) : t.app.common.noTitle),
            invoiceId: e.invoiceId,
            amountStars: e.amountStars,
            date: e.createdAt,
          };
        }),
    [entries, includesInvoice, tc, t],
  );

  const clients: Client[] = useMemo(() => {
    const map = new Map<string, Client>();
    purchases.forEach((p) => {
      const existing = map.get(p.clientKey);
      if (existing) {
        existing.purchases.push(p);
        existing.count += 1;
        existing.total += p.amountStars;
        if (p.date > existing.lastDate) {
          existing.lastDate = p.date;
          existing.lastProduct = p.product;
        }
        if (!existing.username && p.username) existing.username = p.username;
      } else {
        map.set(p.clientKey, {
          key: p.clientKey,
          name: p.clientName,
          username: p.username,
          telegramUserId: p.telegramUserId,
          purchases: [p],
          count: 1,
          total: p.amountStars,
          lastDate: p.date,
          lastProduct: p.product,
          status: 'new',
        });
      }
    });
    const list = Array.from(map.values());
    list.forEach((c) => {
      c.status = c.total >= VIP_THRESHOLD_STARS ? 'vip' : c.count > 1 ? 'returning' : 'new';
      c.purchases.sort((a, b) => b.date.getTime() - a.date.getTime());
    });
    return list;
  }, [purchases]);

  const kpis = useMemo(() => {
    const revenue = purchases.reduce((s, p) => s + p.amountStars, 0);
    return {
      clients: clients.length,
      purchases: purchases.length,
      revenue,
      average: purchases.length ? Math.round(revenue / purchases.length) : 0,
    };
  }, [clients, purchases]);

  /* ---------- Helpers ---------- */
  const formatDate = (d: Date) => d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  const stars = (n: number) => (
    <span className="nt-num">
      {n.toLocaleString(locale)} <span style={{ color: '#9b9a97' }}>★</span>
    </span>
  );

  const statusMeta = (s: Client['status']): { label: string; color: NotionPillColor } => {
    switch (s) {
      case 'vip': return { label: tc.statusVip, color: 'purple' };
      case 'returning': return { label: tc.statusReturning, color: 'green' };
      default: return { label: tc.statusNew, color: 'blue' };
    }
  };

  /* ---------- Colonnes : Clients ---------- */
  const clientColumns: NotionColumn<Client>[] = useMemo(
    () => [
      {
        key: 'name',
        label: tc.colClient,
        type: 'title',
        width: 'minmax(220px, 1.4fr)',
        accessor: (c) => c.name,
        render: (c) => <NotionPerson name={c.name} sub={c.username ? `@${c.username}` : undefined} />,
      },
      {
        key: 'status',
        label: tc.colStatus,
        type: 'select',
        width: 120,
        accessor: (c) => statusMeta(c.status).label,
        render: (c) => {
          const m = statusMeta(c.status);
          return <NotionPill color={m.color}>{m.label}</NotionPill>;
        },
      },
      { key: 'count', label: tc.colPurchases, type: 'number', width: 110, align: 'right', accessor: (c) => c.count },
      {
        key: 'total',
        label: tc.colTotal,
        type: 'number',
        width: 150,
        align: 'right',
        accessor: (c) => c.total,
        render: (c) => stars(c.total),
      },
      {
        key: 'lastProduct',
        label: tc.colLastProduct,
        type: 'multi',
        width: 'minmax(180px, 1fr)',
        accessor: (c) => c.lastProduct,
        render: (c) => <NotionPill color="orange">{c.lastProduct}</NotionPill>,
      },
      { key: 'lastDate', label: tc.colLastPurchase, type: 'date', width: 150, accessor: (c) => c.lastDate, render: (c) => formatDate(c.lastDate) },
      {
        key: 'username',
        label: tc.colUsername,
        type: 'text',
        width: 150,
        accessor: (c) => (c.username ? `@${c.username}` : ''),
        render: (c) =>
          c.username ? (
            <a className="clients-link" href={`https://t.me/${c.username}`} target="_blank" rel="noopener noreferrer">
              @{c.username}
            </a>
          ) : (
            <span className="nt-empty-cell" />
          ),
      },
      { key: 'tgid', label: tc.colTelegramId, type: 'text', width: 140, accessor: (c) => c.telegramUserId || '' },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tc, locale],
  );

  /* ---------- Colonnes : Achats ---------- */
  const purchaseColumns: NotionColumn<Purchase>[] = useMemo(
    () => [
      {
        key: 'product',
        label: tc.colProduct,
        type: 'title',
        width: 'minmax(220px, 1.4fr)',
        accessor: (p) => p.product,
      },
      {
        key: 'client',
        label: tc.colClient,
        type: 'person',
        width: 'minmax(180px, 1fr)',
        accessor: (p) => p.clientName,
        render: (p) => <NotionPerson name={p.clientName} sub={p.username ? `@${p.username}` : undefined} />,
      },
      {
        key: 'status',
        label: tc.colStatus,
        type: 'select',
        width: 110,
        sortable: false,
        accessor: () => tc.statusPaid,
        render: () => <NotionPill color="green">{tc.statusPaid}</NotionPill>,
      },
      {
        key: 'price',
        label: tc.colPrice,
        type: 'number',
        width: 130,
        align: 'right',
        accessor: (p) => p.amountStars,
        render: (p) => stars(p.amountStars),
      },
      { key: 'date', label: tc.colDate, type: 'date', width: 150, accessor: (p) => p.date, render: (p) => formatDate(p.date) },
      { key: 'tgid', label: tc.colTelegramId, type: 'text', width: 140, accessor: (p) => p.telegramUserId || '' },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tc, locale],
  );

  const labels = {
    search: tt.search,
    sort: tt.sort,
    filter: tt.filter,
    open: tt.open,
    count: tt.count,
    empty: activeView === 'clients' ? tc.noClients : tc.noPurchases,
    clearSort: tt.clearSort,
    ascending: tt.ascending,
    descending: tt.descending,
  };

  const views = [
    { key: 'clients', label: tc.viewClients, icon: NotionIcons.person, count: clients.length },
    { key: 'purchases', label: tc.viewPurchases, icon: NotionIcons.table, count: purchases.length },
  ];

  if (error) {
    return <div className="clients-error">{t.app.common.error}: {error}</div>;
  }

  const detailStatus = detail ? statusMeta(detail.status) : null;

  return (
    <div className="clients-page">

      {/* KPIs */}
      <div className="clients-kpis">
        <div className="clients-kpi" style={{ ['--i' as string]: 0 }}>
          <span className="clients-kpi-label">{tc.kpiClients}</span>
          <span className="clients-kpi-value">{loading ? '—' : kpis.clients}</span>
        </div>
        <div className="clients-kpi" style={{ ['--i' as string]: 1 }}>
          <span className="clients-kpi-label">{tc.kpiPurchases}</span>
          <span className="clients-kpi-value">{loading ? '—' : kpis.purchases}</span>
        </div>
        <div className="clients-kpi" style={{ ['--i' as string]: 2 }}>
          <span className="clients-kpi-label">{tc.kpiRevenue}</span>
          <span className="clients-kpi-value">{loading ? '—' : <>{kpis.revenue.toLocaleString(locale)} <em>★</em></>}</span>
        </div>
        <div className="clients-kpi" style={{ ['--i' as string]: 3 }}>
          <span className="clients-kpi-label">{tc.kpiAverage}</span>
          <span className="clients-kpi-value">{loading ? '—' : <>{kpis.average.toLocaleString(locale)} <em>★</em></>}</span>
        </div>
      </div>

      {activeView === 'clients' ? (
        <NotionTable<Client>
          columns={clientColumns}
          rows={clients}
          rowKey={(c) => c.key}
          views={views}
          activeView={activeView}
          onViewChange={(k) => setActiveView(k as ViewKey)}
          onOpen={(c) => setDetail(c)}
          defaultSort={{ key: 'lastDate', dir: 'desc' }}
          loading={loading}
          formatDate={formatDate}
          labels={labels}
        />
      ) : (
        <NotionTable<Purchase>
          columns={purchaseColumns}
          rows={purchases}
          rowKey={(p) => p.id}
          views={views}
          activeView={activeView}
          onViewChange={(k) => setActiveView(k as ViewKey)}
          onOpen={(p) => {
            const c = clients.find((x) => x.key === p.clientKey);
            if (c) setDetail(c);
          }}
          defaultSort={{ key: 'date', dir: 'desc' }}
          loading={loading}
          formatDate={formatDate}
          labels={labels}
        />
      )}

      {/* Fiche client */}
      <Modal open={!!detail} onCancel={() => setDetail(null)} footer={null} width={620} destroyOnClose className="nt-detail-modal">
        {detail && detailStatus && (
          <div className="nt-detail">
            <div className="nt-detail-title">
              <NotionPerson name={detail.name} sub={detail.username ? `@${detail.username}` : undefined} />
            </div>
            <div className="nt-props">
              <div className="nt-prop-label">{NotionIcons.select} {tc.colStatus}</div>
              <div className="nt-prop-value"><NotionPill color={detailStatus.color}>{detailStatus.label}</NotionPill></div>

              <div className="nt-prop-label">{NotionIcons.number} {tc.colPurchases}</div>
              <div className="nt-prop-value">{detail.count}</div>

              <div className="nt-prop-label">{NotionIcons.number} {tc.colTotal}</div>
              <div className="nt-prop-value">{stars(detail.total)}</div>

              <div className="nt-prop-label">{NotionIcons.date} {tc.colLastPurchase}</div>
              <div className="nt-prop-value">{formatDate(detail.lastDate)}</div>

              <div className="nt-prop-label">{NotionIcons.text} {tc.colTelegramId}</div>
              <div className="nt-prop-value">{detail.telegramUserId || <span style={{ color: '#9b9a97' }}>—</span>}</div>

              <div className="nt-prop-label">{NotionIcons.url} {tc.colUsername}</div>
              <div className="nt-prop-value">
                {detail.username ? (
                  <a className="clients-link" href={`https://t.me/${detail.username}`} target="_blank" rel="noopener noreferrer">
                    @{detail.username}
                  </a>
                ) : (
                  <span style={{ color: '#9b9a97' }}>—</span>
                )}
              </div>
            </div>

            <div className="nt-detail-sep" />
            <div className="clients-detail-heading">
              {NotionIcons.table} {tc.purchasesOf} <span className="clients-detail-count">{detail.count}</span>
            </div>
            <div className="nt-detail-list">
              {detail.purchases.map((p) => (
                <div key={p.id} className="nt-detail-item">
                  <div>
                    <div>{p.product}</div>
                    <div className="sub">{formatDate(p.date)}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <NotionPill color="green">{tc.statusPaid}</NotionPill>
                    {stars(p.amountStars)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Clients;
